import { DEFAULT_PRINT_SETTINGS, type PrintSettings } from './printSettings';

const PREVIEW_ROWS = 20;
const PREVIEW_COLUMNS = 12;
const MIN_READABLE_SCALE = 0.5;

export function limitPrintTables(root: ParentNode) {
  for (const table of Array.from(root.querySelectorAll('table'))) {
    const rows = Array.from(table.tBodies).flatMap((body) => Array.from(body.rows));
    const totals = rows.filter((row) => /^grand total$|^total$/i.test(row.cells[0]?.textContent?.trim() ?? ''));
    const details = rows.filter((row) => !totals.includes(row));
    const columns = Math.max(0, ...Array.from(table.rows, (row) => row.cells.length));
    const clipColumns =
      columns > PREVIEW_COLUMNS &&
      Array.from(table.rows).every((row) =>
        Array.from(row.cells).every((cell) => cell.colSpan === 1 && cell.rowSpan === 1),
      );
    if (details.length <= PREVIEW_ROWS && !clipColumns) continue;
    const keep = new Set([...details.slice(0, PREVIEW_ROWS), ...totals]);
    rows.forEach((row) => {
      if (!keep.has(row)) row.remove();
    });
    if (clipColumns) {
      const lastHeader = table.tHead?.rows[table.tHead.rows.length - 1]?.cells[columns - 1]?.textContent?.trim() ?? '';
      const totalColumn = /^grand total$|^total$/i.test(lastHeader);
      for (const row of Array.from(table.rows))
        Array.from(row.cells).forEach((cell, index) => {
          if (index >= (totalColumn ? PREVIEW_COLUMNS - 1 : PREVIEW_COLUMNS) && !(totalColumn && index === columns - 1))
            cell.remove();
        });
    }
    const caption = table.caption ?? table.createCaption();
    caption.textContent = `Table preview: ${Math.min(details.length, PREVIEW_ROWS)} of ${details.length} loaded detail rows${totals.length ? ', plus totals' : ''}${clipColumns ? `; ${PREVIEW_COLUMNS} of ${columns} columns` : ''}. Open the report for full detail.`;
    caption.style.cssText = 'caption-side:bottom;text-align:left;font-size:11px;line-height:1.4;padding:6px 0';
  }
}

export function fitPrintSheet(doc: Document, settings: PrintSettings = DEFAULT_PRINT_SETTINGS) {
  doc.documentElement.style.width = '1600px';
  doc.body.style.width = '1600px';
  const workbook = doc.querySelector('.lp-workbook');
  for (const cell of workbook ? [] : Array.from(doc.querySelectorAll<HTMLElement>('td'))) {
    if ((cell.textContent?.trim().length ?? 0) > 16) continue;
    for (const node of [cell, ...Array.from(cell.querySelectorAll<HTMLElement>('*'))])
      node.style.setProperty('white-space', 'nowrap', 'important');
  }
  const width = Math.max(1600, doc.body.scrollWidth);
  const height = Math.max(1, doc.body.scrollHeight);
  const [short, long] = [210, 297].map((mm) => (mm / 25.4) * 96);
  const portraitScale = Math.min((short - 20) / width, (long - 20) / height);
  const landscapeScale = Math.min((long - 20) / width, (short - 20) / height);
  const orientation =
    settings.orientation === 'auto'
      ? portraitScale >= landscapeScale
        ? 'portrait'
        : 'landscape'
      : settings.orientation;
  const sheetCss = (paper: 'portrait' | 'landscape', zoom: number, paginate: boolean) => {
    const paperWidth = paper === 'portrait' ? short : long;
    return `@page { size: A4 ${paper}; margin: 0; }
    html { width: ${paperWidth}px !important; min-height: 0 !important; }
    body { width: ${width}px !important; min-height: 0 !important; margin: 0 !important; padding: ${10 / zoom}px !important; zoom: ${zoom}; }
    * { break-before: auto !important; break-after: auto !important; break-inside: auto !important; }
    ${paginate ? 'tr, img, svg, canvas, figure { break-inside: avoid !important; }' : ''}
    [data-lens-print-fit] { width: ${width}px !important; min-width: 0 !important; }
    ${
      workbook
        ? ''
        : `table { width: 100% !important; min-width: 0 !important; max-width: 100% !important; }
    th, td, th *, td * { white-space: normal !important; overflow-wrap: normal !important; word-break: normal !important; }`
    }
  `;
  };
  let sheetWidth = orientation === 'portrait' ? short : long;
  let sheetHeight = orientation === 'portrait' ? long : short;
  const scale = orientation === 'portrait' ? portraitScale : landscapeScale;
  const style = doc.createElement('style');
  style.textContent = sheetCss(orientation, scale, false);
  doc.head.append(style);
  let fittedScale = scale;
  for (let attempt = 0; attempt < 3; attempt++) {
    const printedHeight = doc.body.getBoundingClientRect().height;
    if (printedHeight <= sheetHeight - 10) break;
    fittedScale *= (sheetHeight - 20) / printedHeight;
    doc.body.style.setProperty('zoom', String(fittedScale));
  }
  const paginate = fittedScale < MIN_READABLE_SCALE;
  if (paginate) {
    const paper = settings.orientation === 'auto' ? 'landscape' : orientation;
    sheetWidth = paper === 'portrait' ? short : long;
    sheetHeight = paper === 'portrait' ? long : short;
    fittedScale = (sheetWidth - 20) / width;
    doc.body.style.removeProperty('zoom');
    style.textContent = sheetCss(paper, fittedScale, true);
  }
  if (doc.querySelector('[data-lens-report-canvas]')) {
    doc.body.style.setProperty('margin-left', `${Math.max(0, (sheetWidth / fittedScale - width) / 2)}px`, 'important');
  }
  doc.documentElement.setAttribute('data-lens-print-sheet', '');
  doc.documentElement.setAttribute('data-lens-print-width', String(sheetWidth));
  doc.documentElement.setAttribute('data-lens-print-height', String(sheetHeight));
  doc.documentElement.setAttribute('data-lens-print-content-width', String(width));
  doc.documentElement.setAttribute('data-lens-print-image-fit', paginate ? 'content' : settings.imageFit);
}

export async function singleSheetHtml(
  html: string,
  owner: Document,
  settings?: PrintSettings,
  matchPaperViewport = false,
): Promise<string> {
  const frame = owner.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-same-origin');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;left:-10000px;width:1600px;height:1px;border:0;visibility:hidden';
  let fittedSettings = settings;
  try {
    const load = () =>
      new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error('The print sheet did not load.')), 10000);
        frame.onload = () => {
          window.clearTimeout(timer);
          resolve();
        };
        frame.onerror = () => {
          window.clearTimeout(timer);
          reject(new Error('The print sheet did not load.'));
        };
        frame.srcdoc = html;
        if (!frame.isConnected) owner.body.append(frame);
      });
    for (let attempt = 0; attempt < 3; attempt++) {
      await load();
      const doc = frame.contentDocument;
      if (!doc) throw new Error('The print sheet is unavailable.');
      await doc.fonts?.ready;
      fitPrintSheet(doc, fittedSettings);
      const width = Number(doc.documentElement.getAttribute('data-lens-print-width'));
      if (!matchPaperViewport || Math.abs(frame.clientWidth - width) < 1)
        return `<!doctype html>${doc.documentElement.outerHTML}`;
      fittedSettings = {
        ...(settings ?? DEFAULT_PRINT_SETTINGS),
        orientation:
          width < Number(doc.documentElement.getAttribute('data-lens-print-height')) ? 'portrait' : 'landscape',
      };
      frame.style.width = `${width}px`;
    }
    throw new Error('The print sheet could not fit the selected paper.');
  } finally {
    frame.remove();
  }
}
